import { gql } from "@apollo/client";
import { AdminLayout } from "components/admin/admin-layout";
import { Button } from "components/buttons/button";
import {
  inputClassNames,
  labelClassNames,
  StandardInput,
} from "components/forms/input";
import { TagSelectorValue } from "components/forms/tag-selector";
import { saveDraftContent } from "components/text-editor/editor-utils";
import { TextEditor } from "components/text-editor/text-editor";
import currency from "currency.js";
import { EditorState } from "draft-js";
import { useInsertNewProductMutation } from "lib/graphql-generated";
import { useInput } from "lib/hooks/use-input";
import { useRouter } from "next/router";
import { useState } from "react";
import slugify from "slugify";

const INSERT_PRODUCT = gql`
  mutation InsertNewProduct($data: products_insert_input!) {
    insert_products_one(object: $data) {
      title
      id
      price
    }
  }
`;

export const NewProduct = () => {
  const router = useRouter();
  const title = useInput<string>("");
  const description = useInput<EditorState>(null);
  const price = useInput<number>(0);
  const stock = useInput<number>(0);
  const image = useInput<string>("");
  const tags = useInput<TagSelectorValue[]>([]);
  const [shouldTrackStock, setTrackStock] = useState<boolean>(false);
  const [shouldTrackDuration, setShouldtrackDuration] =
    useState<boolean>(false);
  const [save, { error, data, loading }] = useInsertNewProductMutation();
  const [generalLoading, setLoading] = useState<boolean>(false);
  const isSaveDisabled =
    !(title.isModified && price.isModified) || loading || generalLoading;

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Create new stripe product + price

    const {
      data: { insert_products_one },
    } = await save({
      variables: {
        data: {
          title: title.value,
          slug: slugify(title.value, { lower: true, strict: true }),
          price: currency(price.value).intValue,
          stock: shouldTrackStock ? stock.value : null,
          image: image.value,
          description: saveDraftContent(description.value),
        },
      },
    });

    router.push(`/admin/products/${insert_products_one.id}`);

    setLoading(false);
  };

  return (
    <>
      <AdminLayout page='New Book'>
        <article className='space-y-4'>
          <header className='flex flex-row justify-between'>
            <h1 className='header text-4xl'>Creating New Product</h1>
            <section className='space-x-4'>
              <Button
                buttonType='primary'
                buttonSize='md'
                disabled={isSaveDisabled}
                onClick={handleSave}>
                Save
              </Button>
              <Button buttonType='ghost' buttonSize='md' href='/admin/products'>
                Cancel
              </Button>
            </section>
          </header>
          <form
            className='flex flex-row flex-wrap w-full space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-4 xl:gap-8'
            onSubmit={handleSave}>
            <StandardInput
              type='text'
              name='title'
              id='title'
              label='Title'
              bind={{ ...title.bind }}
            />
            <StandardInput
              type='number'
              name='price'
              id='price'
              label='Price'
              bind={{ ...price.bind }}
            />
            <StandardInput
              type='url'
              name='image'
              id='image'
              label='Image URL'
              bind={{ ...image.bind }}
            />

            <section>
              <label
                htmlFor='stock'
                className={`w-full flex flex-col ${labelClassNames}`}>
                Stock
                <input
                  type='number'
                  name='stock'
                  id='stock'
                  {...stock.bind}
                  disabled={!shouldTrackStock}
                  className={`${inputClassNames}`}
                />
              </label>

              <label htmlFor='useStock' className={`${labelClassNames}`}>
                <input
                  type='checkbox'
                  name='useStock'
                  id='useStock'
                  className='mr-2 '
                  checked={shouldTrackStock}
                  onChange={(e) => {
                    setTrackStock(e.target.checked);
                  }}
                />
                Track Stock
              </label>
            </section>

            <label
              htmlFor='description'
              className={`w-full col-span-full ${labelClassNames}`}>
              Description{" "}
              <TextEditor
                state={description.value}
                setState={description.setValue}
              />
            </label>
          </form>
        </article>
      </AdminLayout>
    </>
  );
};

export default NewProduct;
